import InstrumentInfoContent from '@/components/common/InstrumentInfoContent';
import { roundedFullClassname } from '@/helpers/environmentHelper';
import { useAppSelector } from '@/state/hooks';
import { selectAccountType } from '@/state/reducers/authSlice';
import { InstrumentConfig } from '@/state/reducers/ohoSlice';
import { TraderMarket } from '@/state/reducers/traderMarketSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import { Combobox, Transition } from '@headlessui/react';
import cn from 'classnames';
import { CSSProperties, Fragment, useCallback, useMemo, useState } from 'react';
import { MdExpandMore, MdInfoOutline } from 'react-icons/md';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';

interface Option {
    label: string;
    value?: string;
    market?: TraderMarket;
    config?: InstrumentConfig;
}

interface SwitchActivePairProps {
    options: Option[];
    selected?: Option;
    onSubmit: (e: TraderMarket) => void;
}

const SwitchActivePair = (props: SwitchActivePairProps) => {
    const { options, selected, onSubmit } = props;
    const [query, setQuery] = useState<any>('');
    const [categoryFilter, setCategoryFilter] = useState<any>('All');
    const [instrumentInfoOption, setInstrumentInfoOption] = useState<Option>();

    const lang = useLanguage();
    const accountType = useAppSelector(selectAccountType);

    const filteredOptions = useMemo(() => {
        if (query) {
            const temp = query.replace('/', '').toUpperCase();
            return options
                .filter((option) => {
                    return (
                        option.label.toUpperCase().replace('/', '').includes(temp) ||
                        option.config?.description?.toUpperCase().includes(temp)
                    );
                })
                .filter((option) => option.config?.type === categoryFilter || categoryFilter === 'All');
        }
        return options.filter((option) => option.config?.type === categoryFilter || categoryFilter === 'All');
    }, [options, query, categoryFilter]);

    const categoryOptions = useMemo(() => {
        return [
            { label: lang.common.showAll, value: 'All' },
            { label: accountType === 'NOP' ? lang.common.cryptoSpot : lang.common.cryptoCFD, value: 'Crypto' },
            { label: lang.common.fxSpot, value: 'FX' },
            { label: lang.common.indexCFD, value: 'Index' },
            { label: lang.common.commCFD, value: 'Commodity' },
            { label: lang.common.futuresCFD, value: 'Future' }
        ];
    }, [lang, accountType]);

    const filteredCategoryOptions = useMemo(() => {
        return categoryOptions.filter((category) => {
            const hasInCategory = options.some((option) => option.config?.type === category.value);

            return category.value === 'All' || hasInCategory;
        });
    }, [options, categoryOptions]);

    const VirtualizedOption = useCallback(
        ({ index, style }: ListItemProps) => {
            const option = filteredOptions[index];
            const type = option.config?.type;

            return (
                <Combobox.Option
                    style={style}
                    className="relative cursor-pointer select-none py-2 pl-3 pr-4 group hover:bg-brand-primary text-brand-text"
                    value={option}>
                    <div>
                        <div className="flex items-center gap-1">
                            {/* <InstrumentInfoModal market={option?.market} /> */}
                            <div
                                title="Instrument Info"
                                className={cn('h-full flex items-center p-1 cursor-pointer')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setInstrumentInfoOption(option);
                                }}>
                                <MdInfoOutline className="w-3 h-3" />
                            </div>
                            <span className="block truncate text-sm">{option?.label}</span>
                        </div>
                        <span className="text-xs text-brand-neutral-400 group-hover:text-brand-text">
                            {type === 'FX' && lang.common.fxSpot}
                            {type === 'Crypto' &&
                                `${accountType === 'NOP' ? lang.common.cryptoSpot : lang.common.cryptoCFD}`}
                            {type === 'Index' && lang.common.indexCFD}
                            {type === 'Commodity' && lang.common.commCFD}
                            {type === 'Future' && lang.common.futuresCFD}
                            {!type && '-'}
                        </span>
                    </div>
                </Combobox.Option>
            );
        },
        [filteredOptions, accountType]
    );

    const VirtualizedList = useCallback(
        () => (
            <AutoSizer>
                {({ height, width }) => (
                    <VariableSizeList
                        height={height}
                        width={width}
                        itemCount={filteredOptions.length}
                        itemSize={() => 56}>
                        {VirtualizedOption}
                    </VariableSizeList>
                )}
            </AutoSizer>
        ),
        [filteredOptions]
    );

    return (
        <Combobox
            value={selected}
            onChange={(option) => {
                if (option.market) {
                    onSubmit(option.market);
                    setQuery('');
                }
            }}>
            <div
                draggable
                className="relative"
                onDragStart={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                }}>
                <div
                    className={cn(
                        'relative w-full cursor-default overflow-hidden bg-brand-background-dark text-left sm:text-xs',
                        roundedFullClassname
                    )}>
                    <Combobox.Input
                        className={cn(
                            'w-full border border-brand-neutral-700 py-1 text-xs leading-5 text-brand-text bg-brand-background outline-none placeholder:text-brand-neutral-500 rounded-sm',
                            {
                                'pl-3 pr-10': lang.dir === 'ltr',
                                'pl-10 pr-3': lang.dir === 'rtl'
                            }
                        )}
                        autoComplete="off"
                        autoCorrect="off"
                        placeholder={selected?.label}
                        onChange={(event) => setQuery(event.target.value)}
                    />
                    <Combobox.Button>
                        <div
                            className={cn('absolute inset-y-0 flex items-center', {
                                'right-0 pr-2': lang.dir === 'ltr',
                                'left-0 pl-2': lang.dir === 'rtl'
                            })}>
                            <MdExpandMore className="h-5 w-5 text-brand-neutral-400" aria-hidden="true" />
                        </div>
                    </Combobox.Button>
                </div>
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <Combobox.Options
                        className={cn(
                            'absolute mt-2.5 z-[60] h-[300px] flex cursor-default bg-brand-background-dark border border-brand-neutral-700 text-sm shadow-lg shadow-brand-neutral-900 overflow-clip',
                            {
                                'right-0': lang.dir === 'ltr',
                                'left-0': lang.dir === 'rtl'
                            }
                        )}>
                        {!instrumentInfoOption && (
                            <div className="flex flex-col text-xs text-brand-text w-fit border-r border-brand-neutral-700">
                                {filteredCategoryOptions.map((option, optionIdx) => {
                                    return (
                                        <div
                                            key={optionIdx}
                                            className={cn('cursor-pointer hover:bg-brand-primary p-2', {
                                                'bg-brand-primary': categoryFilter === option.value
                                            })}
                                            onClick={() => setCategoryFilter(option.value)}>
                                            {option.label}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {instrumentInfoOption && (
                            <div className="w-full xsm:w-auto border-b xsm:border-b-0 xsm:border-r border-brand-neutral-700 text-xs whitespace-nowrap flex flex-row xsm:flex-col">
                                <div className="p-2 xsm:bg-brand-primary"> {lang.common.instrumentInfo}</div>
                                <div className="py-2 xsm:hidden">-</div>
                                <div className="p-2 xsm:mt-[1px]">{instrumentInfoOption.label}</div>
                            </div>
                        )}
                        <div
                            className={cn('flex flex-col', {
                                'w-[203px]': !instrumentInfoOption,
                                'w-auto': instrumentInfoOption
                            })}>
                            {instrumentInfoOption && (
                                <InstrumentInfoContent
                                    option={instrumentInfoOption}
                                    onClose={() => setInstrumentInfoOption(undefined)}
                                    onSubmit={onSubmit}
                                    flexWrap
                                />
                            )}
                            {!instrumentInfoOption && (
                                <Fragment>
                                    {filteredOptions.length === 0 && query !== '' ? (
                                        <div className="relative select-none py-2 px-4 text-brand-text">
                                            {lang.common.nothingFound}
                                        </div>
                                    ) : (
                                        <VirtualizedList />
                                    )}
                                </Fragment>
                            )}
                        </div>
                    </Combobox.Options>
                </Transition>
            </div>
        </Combobox>
    );
};

export default SwitchActivePair;

export interface ListItemProps {
    index: number;
    style: CSSProperties;
}
